import TagManager from 'react-gtm-module';
import React, { useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import AuthContext from './AuthContext';
import api from 'services/api';

const TrackingContext = React.createContext<{
  actions: {
    trackSignIn: (accountId?: string) => void;
    trackSignUpStart: () => void;
    trackSignUpComplete: (accountId: string) => void;
    trackFailedSignUp: (error: string) => void;
    trackAccountDeletion: (accountId: string) => void;
    sendGA4ClientId: (authToken: string) => Promise<boolean>;
    getGA4ClientId: () => string | null;
    getGA4ClientIdAsync: () => Promise<string | null>;
  };
}>({
  actions: {
    trackSignIn: (accountId?: string) => {},
    trackSignUpStart: () => {},
    trackSignUpComplete: () => {},
    trackFailedSignUp: () => {},
    trackAccountDeletion: () => {},
    sendGA4ClientId: async () => false,
    getGA4ClientId: () => null,
    getGA4ClientIdAsync: async () => null,
  },
});

interface TrackingProviderProps {
  children: React.ReactNode;
}

export function TrackingProvider(
  props: TrackingProviderProps
): React.ReactElement {
  const { children } = props;
  const location = useLocation();
  const { data: { account } } = useContext(AuthContext);

  const environment = process.env.REACT_APP_ENVIRONMENT;
  const accountId = account?.id?.toString() || '';

  useEffect(() => {
    const gtmId = process.env.REACT_APP_GTM_ID;

    if (gtmId) {
      const tagManagerArgs = {
        gtmId: gtmId,
        dataLayer: { 
          environment,
          accountId: accountId || undefined 
        },
      };
      TagManager.initialize(tagManagerArgs);
    }
  }, [environment, accountId]);

  // Push event to dataLayer
  const pushToDataLayer = (eventData: {
    event: string;
    eventCategory: string;
    eventAction: string;
    eventLabel?: string;
    accountId?: string;
    [key: string]: any;
  }) => {
    const finalAccountId = eventData.accountId || accountId || undefined;

    // Send to GTM dataLayer
    TagManager.dataLayer({
      dataLayer: {
        ...eventData,
        environment,
        accountId: finalAccountId,
      },
    });
  };

  useEffect(() => {
    // Track page views via dataLayer
    TagManager.dataLayer({
      dataLayer: {
        event: 'page_view',
        page: location.pathname + location.search,
        environment,
        accountId: accountId || undefined
      },
    });
  }, [location, accountId, environment]);

  /**
   * Extract GA4 client ID from gtag (async version)
   * @returns {Promise<string|null>} GA4 client ID or null if not found
   */
  const getGA4ClientIdAsync = (): Promise<string | null> => {
    return new Promise((resolve) => {
      try {
        if (typeof window !== 'undefined' && typeof (window as any).gtag !== 'undefined') {
          const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
          if (measurementId) {
            (window as any).gtag('get', measurementId, 'client_id', (clientId: string) => {
              resolve(clientId || null);
            });
            // Timeout after 2 seconds
            setTimeout(() => resolve(null), 2000);
            return;
          }
        }
        resolve(null);
      } catch (error) {
        console.warn('GA4 Integration: Error getting client ID from gtag (async):', error);
        resolve(null);
      }
    });
  };

  /**
   * Extract GA4 client ID (synchronous fallback)
   * @returns {string|null} GA4 client ID in format "123456789.1234567890" or null if not found
   */
  const getGA4ClientId = (): string | null => {
    try {
      // Method 1: Parse cookies, looking for the one most likely to be from our domain
      const allCookies = document.cookie.split('; ');
      const gaCookies = allCookies.filter(cookie => cookie.startsWith('_ga='));
      
      if (gaCookies.length > 0) {
        // Use the first _ga cookie we find
        // In most cases, this will be the one for the current domain
        const gaCookie = gaCookies[0];
        
        // _ga cookie format: GA1.{scope}.{client_id_part1}.{client_id_part2}
        const cookieValue = gaCookie.split('=')[1];
        const parts = cookieValue.split('.');
        if (parts.length >= 4) {
          return `${parts[2]}.${parts[3]}`;
        }
      }

      // Method 2: Try to use Google Analytics object directly (legacy fallback)
      if (typeof window !== 'undefined' && (window as any).ga && (window as any).ga.getAll) {
        const trackers = (window as any).ga.getAll();
        if (trackers && trackers.length > 0) {
          return trackers[0].get('clientId');
        }
      }

      return null;
    } catch (error) {
      console.warn('GA4 Integration: Error extracting client ID:', error);
      return null;
    }
  };

  /**
   * Send GA4 client ID to backend
   * @param {string} authToken - JWT auth token
   * @returns {Promise<boolean>} Success status
   */
  const sendGA4ClientId = async (authToken: string): Promise<boolean> => {
    try {
      if (!authToken) {
        console.warn('GA4 Integration: No auth token provided');
        return false;
      }

      // Try async method first (preferred for GA4)
      let clientId = await getGA4ClientIdAsync();
      
      // Fall back to synchronous method
      if (!clientId) {
        clientId = getGA4ClientId();
      }
      
      if (!clientId) {
        console.warn('GA4 Integration: No client ID available');
        return false;
      }

      const response = await api.put(
        '/account/user/settings/ga4_client_id',
        { ga4_client_id: clientId },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );

      if (response.status === 200) {
        console.log('GA4 Integration: Client ID sent successfully', { clientId });
        return true;
      } else {
        console.error('GA4 Integration: Failed to send client ID', response.status);
        return false;
      }
    } catch (error) {
      console.error('GA4 Integration: Error sending client ID:', error);
      return false;
    }
  };

  const trackSignIn = (dynamicAccountId?: string) => {
    pushToDataLayer({
      event: 'sign_in',
      eventCategory: 'Authentication',
      eventAction: 'Sign In',
      ...(dynamicAccountId && { accountId: dynamicAccountId })
    });
  };

  const trackSignUpStart = () => {
    pushToDataLayer({
      event: 'sign_up_started',
      eventCategory: 'Authentication',
      eventAction: 'Sign Up Started',
    });
  };

  const trackSignUpComplete = (accountId: string) => {
    pushToDataLayer({
      event: 'sign_up_completed',
      eventCategory: 'Authentication',
      eventAction: 'Sign Up Completed',
      eventLabel: `Account ID: ${accountId}`,
      accountId: accountId,
    });
  };

  const trackFailedSignUp = (error: string) => {
    pushToDataLayer({
      event: 'sign_up_failed',
      eventCategory: 'Authentication',
      eventAction: 'Sign Up Failed',
      eventLabel: error,
      errorMessage: error,
    });
  };

  const trackAccountDeletion = (accountId: string) => {
    pushToDataLayer({
      event: 'account_deletion',
      eventCategory: 'Account',
      eventAction: 'Deletion',
      eventLabel: `Account ID: ${accountId}`,
      accountId: accountId,
    });
  };

  return (
    <TrackingContext.Provider
      value={{
        actions: {
          trackSignIn,
          trackSignUpStart,
          trackSignUpComplete,
          trackFailedSignUp,
          trackAccountDeletion,
          sendGA4ClientId,
          getGA4ClientId,
          getGA4ClientIdAsync,
        },
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
}
export default TrackingContext;
